import type { FC } from 'react';
import { useEffect } from 'react';
import { useFormContext } from 'react-hook-form';
import { Alert, Box, Button, Skeleton, Typography } from '@mui/material';
import { isEmpty } from 'lodash-es';
import { Select } from 'components/common';
import type { AdSettingPageEntry, AdSettingTextEntry, AdSettingsRequestAdSet } from '../api';
import useAdSettings from '../api/getAdSettings';
import adSettingsMapper from '../utils/adSettings.mapper';

interface AdSettingsFieldsSectionProps {
  settingsIndex: number;
  app: string;
  platform: string;
  adSets: AdSettingsRequestAdSet[];
  isPartnershipAds: boolean;
  partnershipId?: string;
  appPageId?: string;
  sectionTitle?: string;
}

const AdSettingsFieldsSection: FC<AdSettingsFieldsSectionProps> = ({
  settingsIndex,
  app,
  platform,
  adSets,
  isPartnershipAds,
  partnershipId,
  appPageId = '',
  sectionTitle,
}) => {
  const { setValue, trigger } = useFormContext();
  const normalizedAppPageId = (appPageId ?? '').trim();
  const isPartnershipSelected = Boolean(partnershipId?.trim());
  const canFetchPartnershipAdSettings =
    !isPartnershipAds || (isPartnershipSelected && Boolean(normalizedAppPageId));

  const {
    data: adSettings,
    isError,
    error,
    isSuccess: isSuccessAdSettings,
    isLoading: isLoadingAdSettings,
    refetch: refetchAdSettings,
  } = useAdSettings({
    params: { app, platform, ad_sets: adSets },
    isPartnershipAds,
    appPageId: normalizedAppPageId,
    config: {
      select: adSettingsMapper,
      enabled: canFetchPartnershipAdSettings,
    },
  });

  useEffect(() => {
    if (!canFetchPartnershipAdSettings) {
      setValue(`selectedAdSettings.${settingsIndex}`, {}, { shouldValidate: false });
      return;
    }

    if (!isSuccessAdSettings || !adSettings) {
      return;
    }

    const selectedSettings = adSettings.reduce(
      (acc, { name, pages, texts }) => {
        const defaultPage = pages?.find(
          (page: AdSettingPageEntry) => page.is_default,
        );
        const defaultText = texts?.find(
          (text: AdSettingTextEntry) => text.is_default,
        );

        return {
          ...acc,
          [name]: {
            page_id: defaultPage?.id ?? '',
            text_id: defaultText?.id ?? '',
          },
        };
      },
      {},
    );

    if (!isEmpty(selectedSettings)) {
      setValue(`selectedAdSettings.${settingsIndex}`, selectedSettings, {
        shouldValidate: false,
      });
      void trigger('selectedAdSettings');
    }
  }, [
    adSettings,
    canFetchPartnershipAdSettings,
    isSuccessAdSettings,
    setValue,
    settingsIndex,
    trigger,
  ]);

  if (isPartnershipAds && !canFetchPartnershipAdSettings) {
    return (
      <Alert severity="info" variant="standard" sx={{ width: 'fit-content' }}>
        Ad settings are not loaded because partnership page is not selected.
      </Alert>
    )
  }

  if (isLoadingAdSettings) {
    return (
        <Skeleton
          height={40}
          width={'50%'}
          sx={{ transform: 'translateY(0)', borderRadius: '8px' }}
        />
    );
  }

  if (isError) {
    return (
      <Alert
        severity="error"
        variant="standard"
        sx={{ width: 'fit-content' }}
        action={(
          <Button
            color="inherit"
            size="small"
            onClick={() => {
              void refetchAdSettings();
            }}
          >
            Retry
          </Button>
        )}
      >
        {(error as { cause?: { error?: string } })?.cause?.error ??
          'Error occurred. Please try again.'
        }
      </Alert>
    );
  }

  if ((!adSettings || isEmpty(adSettings)) && !isError && canFetchPartnershipAdSettings) {
    return (
      <Alert severity="error" variant="standard" sx={{ width: 'fit-content' }}>
        Ad settings not found for selected ad sets.
      </Alert>
    );
  }

  return (
    <Box sx={{ display: 'flex', flexDirection: 'column', gap: 2 }}>
      {sectionTitle && (
        <Typography variant="subtitle2" fontWeight={500}>
          {sectionTitle}
        </Typography>
      )}
      <Box
        sx={{ display: 'flex', flexDirection: 'column', gap: 2 }}
        data-testid={`adSettingsForm-${settingsIndex}`}
      >
        {adSettings?.map((setting, i) => (
          <Box
            key={`${setting.name}.${i}`}
            sx={{ display: 'flex', flexDirection: 'column', gap: 2 }}
          >
            <Typography variant={'body2'}>{setting.name}</Typography>
            <Box
              sx={{
                display: 'flex',
                flexDirection: 'column',
                flexGrow: 1,
                gap: 2,
              }}
            >
              <Box sx={{ width: '100%', display: isPartnershipAds ? 'none' : 'block' }}>
                <Select
                  disabled={!setting?.pages?.length}
                  name={`selectedAdSettings.${settingsIndex}.${setting.name}.page_id`}
                  sx={{ '.MuiInputBase-root': { backgroundColor: 'white' } }}
                  options={
                    setting?.pages?.map((s) => ({
                      value: s.id,
                      label: s.name,
                    })) ?? []
                  }
                  inputLabel={'Page'}
                />
              </Box>
              <Box sx={{ width: '100%' }}>
                <Select
                  disabled={!setting?.texts?.length}
                  sx={{ '.MuiInputBase-select': { whiteSpace: 'wrap' }, '.MuiInputBase-root': { backgroundColor: 'white' } }}
                  name={`selectedAdSettings.${settingsIndex}.${setting.name}.text_id`}
                  options={
                    setting?.texts?.map((s) => ({
                      value: s.id,
                      label: s.name,
                    })) ?? []
                  }
                  inputLabel={'Text'}
                />
              </Box>
            </Box>
          </Box>
        ))}
      </Box>
    </Box>
  );
};

export default AdSettingsFieldsSection;
